import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft } from '@phosphor-icons/react';
import api from '../lib/api';

const PASSWORD_MIN = 8;

export default function OrgResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) { setValidating(false); return; }
    api.get(`/organiser/password/reset/validate?token=${encodeURIComponent(token)}`)
      .then(() => setValid(true))
      .catch(() => setValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const tooShort = password.length > 0 && password.length < PASSWORD_MIN;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= PASSWORD_MIN && password === confirm && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/organiser/password/reset', { token, new_password: password });
      navigate('/login?reset=1');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      if (detail.includes('expired') || detail.includes('Request a new one')) {
        setValid(false);
      } else {
        setError(detail);
      }
    } finally { setSubmitting(false); }
  };

  const expiredView = (
    <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4" data-testid="reset-expired">
      <div className="w-14 h-14 bg-[#FFF3E0] rounded-full flex items-center justify-center mx-auto">
        <svg className="w-7 h-7 text-[#E59000]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      </div>
      <h2 className="text-xl font-bold text-[#1D242B]">Link expired</h2>
      <p className="text-sm text-[#455561]">This link has expired. Request a new one.</p>
      <Link to="/organiser/forgot-password" className="inline-block bg-[#1B4332] text-white px-6 py-3 rounded-xl font-semibold text-sm hover:bg-[#163B2B] transition-colors" data-testid="reset-request-new">Request new link</Link>
    </Card>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F5] flex flex-col items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md mb-4">
        <Link to="/home" className="inline-flex items-center gap-1.5 text-sm text-[#455561] font-semibold hover:text-[#1B4332] transition-colors" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" weight="bold" /> Back to home
        </Link>
      </div>

      {validating ? (
        <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center">
          <div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-[#455561] mt-4">Checking link...</p>
        </Card>
      ) : !valid ? (
        expiredView
      ) : (
        <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl space-y-6" data-testid="reset-form">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#1D242B]">Set a new password</h1>
            <p className="text-sm text-[#455561] mt-1">Choose a strong password for your Fore Tour account</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-[#455561] mb-1 block">New password</label>
              <Input type="password" value={password} onChange={e => { setPassword(e.target.value); setError(''); }} placeholder="At least 8 characters" required minLength={PASSWORD_MIN} data-testid="reset-password" />
              {tooShort && <p className="text-xs text-[#D32F2F] mt-1">Must be at least {PASSWORD_MIN} characters</p>}
            </div>
            <div>
              <label className="text-sm font-semibold text-[#455561] mb-1 block">Confirm new password</label>
              <Input type="password" value={confirm} onChange={e => { setConfirm(e.target.value); setError(''); }} placeholder="Type it again" required data-testid="reset-confirm" />
              {mismatch && <p className="text-xs text-[#D32F2F] mt-1">Passwords don't match</p>}
            </div>
            {error && <p className="text-sm text-[#D32F2F] text-center" data-testid="reset-error">{error}</p>}
            <Button type="submit" disabled={!canSubmit} className="w-full h-12 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl font-semibold" data-testid="reset-submit">
              {submitting ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}
